import React from 'react';
import { 
    Box, 
    Container, 
    Typography, 
    Card, 
    CardContent, 
    Button,
    Divider,
    Alert,
    Stack
} from '@mui/material';
import { 
    InfoOutlined as InfoIcon,
    EmailOutlined as EmailIcon,
    HomeOutlined as HomeIcon
} from '@mui/icons-material';
import { useVisualTheme, useBranding } from '../context/DomainContext';

const CourtesyPage = () => {
    const visualTheme = useVisualTheme();
    const { projectName, brandName } = useBranding();
    
    // Stili basati sul visual theme corrente
    const pageStyles = {
        minHeight: '100vh',
        backgroundColor: visualTheme.primary_colors?.background || '#F8F9FA',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
    };

    const cardStyles = {
        maxWidth: 600,
        width: '100%',
        backgroundColor: visualTheme.primary_colors?.header || '#FFFFFF',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        borderRadius: '12px'
    };

    const primaryColor = visualTheme.secondary_colors?.accent || '#0D6EFD';
    const textPrimary = visualTheme.text_colors?.primary || '#212529';
    const textSecondary = visualTheme.text_colors?.secondary || '#6C757D';

    const handleContactSupport = () => {
        window.open('mailto:support@tp-agents.it?subject=Richiesta Configurazione Dominio', '_blank');
    };

    const handleGoHome = () => {
        window.location.href = 'https://tp-agents.it';
    };

    return (
        <Box sx={pageStyles}>
            <Container maxWidth="sm">
                <Card sx={cardStyles}>
                    <CardContent sx={{ padding: '40px 32px' }}>
                        {/* Header con icona */}
                        <Box sx={{ textAlign: 'center', marginBottom: '32px' }}>
                            <Box
                                sx={{
                                    width: 80,
                                    height: 80,
                                    borderRadius: '50%',
                                    backgroundColor: `${primaryColor}15`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 24px auto'
                                }}
                            >
                                <InfoIcon 
                                    sx={{ 
                                        fontSize: 40, 
                                        color: primaryColor 
                                    }} 
                                />
                            </Box>
                            
                            <Typography 
                                variant="h4" 
                                component="h1"
                                sx={{ 
                                    color: textPrimary,
                                    fontWeight: 600,
                                    marginBottom: '12px',
                                    fontFamily: visualTheme.font_definitions?.primary_font || 'Inter, sans-serif'
                                }}
                            >
                                Dominio Non Configurato
                            </Typography>
                            
                            <Typography 
                                variant="h6" 
                                sx={{ 
                                    color: textSecondary,
                                    fontWeight: 400,
                                    lineHeight: 1.5
                                }}
                            >
                                Il dominio che stai visitando non è ancora configurato nel nostro sistema
                            </Typography>
                        </Box>

                        <Divider sx={{ marginBottom: '32px' }} />

                        {/* Messaggio principale */}
                        <Alert 
                            severity="info" 
                            icon={<InfoIcon />}
                            sx={{ 
                                marginBottom: '24px',
                                backgroundColor: `${primaryColor}08`,
                                border: `1px solid ${primaryColor}20`,
                                '& .MuiAlert-icon': {
                                    color: primaryColor
                                }
                            }}
                        >
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                Questo dominio non è ancora stato autorizzato o configurato per accedere alla piattaforma {projectName || brandName || 'TP-Agents'}.
                            </Typography>
                        </Alert>

                        {/* Informazioni aggiuntive */}
                        <Box sx={{ marginBottom: '32px' }}>
                            <Typography 
                                variant="body1" 
                                sx={{ 
                                    color: textPrimary,
                                    marginBottom: '16px',
                                    lineHeight: 1.6
                                }}
                            >
                                <strong>Cosa puoi fare:</strong>
                            </Typography>
                            
                            <Stack spacing={2}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                    <Box 
                                        sx={{ 
                                            width: 6, 
                                            height: 6, 
                                            borderRadius: '50%', 
                                            backgroundColor: primaryColor,
                                            marginTop: '8px',
                                            flexShrink: 0
                                        }} 
                                    />
                                    <Typography variant="body2" sx={{ color: textSecondary }}>
                                        Contatta il nostro team di supporto per richiedere la configurazione del tuo dominio
                                    </Typography>
                                </Box>
                                
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                    <Box 
                                        sx={{ 
                                            width: 6, 
                                            height: 6, 
                                            borderRadius: '50%', 
                                            backgroundColor: primaryColor,
                                            marginTop: '8px',
                                            flexShrink: 0
                                        }} 
                                    />
                                    <Typography variant="body2" sx={{ color: textSecondary }}>
                                        Verifica di aver inserito l'URL corretto nel browser
                                    </Typography>
                                </Box>
                                
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                    <Box 
                                        sx={{ 
                                            width: 6, 
                                            height: 6, 
                                            borderRadius: '50%', 
                                            backgroundColor: primaryColor,
                                            marginTop: '8px',
                                            flexShrink: 0
                                        }} 
                                    />
                                    <Typography variant="body2" sx={{ color: textSecondary }}>
                                        Se sei un amministratore, accedi al pannello di controllo per configurare questo dominio
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>

                        <Divider sx={{ marginBottom: '32px' }} />

                        {/* Azioni */}
                        <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }}>
                            <Button
                                variant="contained"
                                startIcon={<EmailIcon />}
                                onClick={handleContactSupport}
                                sx={{
                                    backgroundColor: primaryColor,
                                    color: '#FFFFFF',
                                    borderRadius: visualTheme.component_styles?.button?.border_radius || '4px',
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    padding: '12px 24px',
                                    '&:hover': {
                                        backgroundColor: `${primaryColor}DD`
                                    }
                                }}
                                fullWidth
                            >
                                Contatta Supporto
                            </Button>
                            
                            <Button
                                variant="outlined"
                                startIcon={<HomeIcon />}
                                onClick={handleGoHome}
                                sx={{
                                    borderColor: primaryColor,
                                    color: primaryColor,
                                    borderRadius: visualTheme.component_styles?.button?.border_radius || '4px',
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    padding: '12px 24px',
                                    '&:hover': {
                                        borderColor: primaryColor,
                                        backgroundColor: `${primaryColor}08`
                                    }
                                }}
                                fullWidth
                            >
                                Vai al Sito Principale
                            </Button>
                        </Stack>

                        {/* Footer informativo */}
                        <Box sx={{ marginTop: '32px', textAlign: 'center' }}>
                            <Typography 
                                variant="caption" 
                                sx={{ 
                                    color: textSecondary,
                                    fontSize: '0.75rem'
                                }}
                            >
                                Dominio visitato: <strong>{window.location.hostname}</strong>
                            </Typography>
                            <br />
                            <Typography 
                                variant="caption" 
                                sx={{ 
                                    color: textSecondary,
                                    fontSize: '0.75rem'
                                }}
                            >
                                Powered by {projectName || brandName || 'TP-Agents'}
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            </Container>
        </Box>
    );
};

export default CourtesyPage;